import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { kuendigungenApi } from '../api';

// ─── Module-level constants ───────────────────────────────

const PLATZHALTER_HINT =
  '{{DATUM}}, {{EMPFAENGER_NAME}}, {{EMPFAENGER_ANSCHRIFT}}, {{KUNDENNUMMER}}, ' +
  '{{KUENDIGUNGSDATUM}}, {{VERTRAGSBEZEICHNUNG}}, {{ABSENDER_NAME}}, {{ABSENDER_ANSCHRIFT}}';

const PLATZHALTER_JSON_EXAMPLE =
  `[\n  {"name": "DATUM", "beschreibung": "Aktuelles Datum"},\n  {"name": "EMPFAENGER_NAME", "beschreibung": "Name des Empfängers"}\n]`;

const TEXT_EXAMPLE =
  `Sehr geehrte Damen und Herren,\n\nhiermit kündige ich meinen Vertrag ` +
  `{{VERTRAGSBEZEICHNUNG}} mit der Kundennummer {{KUNDENNUMMER}} fristgerecht ` +
  `zum {{KUENDIGUNGSDATUM}}.\n\nBitte bestätigen Sie mir den Eingang dieser ` +
  `Kündigung sowie das Vertragsende schriftlich.\n\nMit freundlichen Grüßen\n{{ABSENDER_NAME}}`;

const EMPTY_FORM = {
  name: '',
  beschreibung: '',
  betreff: '',
  text: '',
  platzhalter_json: '',
};

// ─── Helpers ──────────────────────────────────────────────

function parsePlatzhalter(json) {
  if (!json) return [];
  try { return JSON.parse(json); } catch { return []; }
}

function formatDate(value) {
  if (!value) return '–';
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium' }).format(d);
}

function validateForm(form) {
  if (!form.name.trim()) return 'Name ist ein Pflichtfeld.';
  if (!form.betreff.trim()) return 'Betreff ist ein Pflichtfeld.';
  if (!form.text.trim()) return 'Vorlagentext ist ein Pflichtfeld.';
  if (form.platzhalter_json.trim()) {
    try { JSON.parse(form.platzhalter_json); } catch {
      return 'Platzhalter-JSON ist ungültig. Bitte gültiges JSON eingeben oder das Feld leer lassen.';
    }
  }
  return null;
}

// ─── Main Page ────────────────────────────────────────────

export default function KuendigungsVorlagenPage() {
  const navigate = useNavigate();
  const [vorlagen, setVorlagen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // New form state
  const [showNew, setShowNew] = useState(false);
  const [newForm, setNewForm] = useState(EMPTY_FORM);
  const [newSaving, setNewSaving] = useState(false);
  const [newError, setNewError] = useState('');

  // Edit state
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await kuendigungenApi.vorlagen.list();
      setVorlagen(res.data.vorlagen || []);
    } catch {
      setError('Vorlagen konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const systemVorlagen = vorlagen.filter(v => v.system_vorlage === 1 || v.system_vorlage === true);
  const meineVorlagen = vorlagen.filter(v => !v.system_vorlage);

  // ── New form handlers ──

  const handleNewChange = (field, value) =>
    setNewForm(prev => ({ ...prev, [field]: value }));

  const openNewForm = () => {
    setShowNew(true);
    setNewForm(EMPTY_FORM);
    setNewError('');
  };

  const cancelNew = () => {
    setShowNew(false);
    setNewForm(EMPTY_FORM);
    setNewError('');
  };

  const handleNewSubmit = async (e) => {
    e.preventDefault();
    const validationError = validateForm(newForm);
    if (validationError) { setNewError(validationError); return; }
    setNewSaving(true);
    setNewError('');
    try {
      await kuendigungenApi.vorlagen.create({
        ...newForm,
        platzhalter_json: newForm.platzhalter_json.trim() || null,
      });
      cancelNew();
      await load();
    } catch {
      setNewError('Vorlage konnte nicht gespeichert werden.');
    } finally {
      setNewSaving(false);
    }
  };

  // ── Edit handlers ──

  const startEdit = (v) => {
    setEditId(v.id);
    setEditForm({
      name: v.name || '',
      beschreibung: v.beschreibung || '',
      betreff: v.betreff || '',
      text: v.text || '',
      platzhalter_json: v.platzhalter_json || '',
    });
    setEditError('');
  };

  const cancelEdit = () => {
    setEditId(null);
    setEditForm(EMPTY_FORM);
    setEditError('');
  };

  const handleEditChange = (field, value) =>
    setEditForm(prev => ({ ...prev, [field]: value }));

  const handleEditSave = async (id) => {
    const validationError = validateForm(editForm);
    if (validationError) { setEditError(validationError); return; }
    setEditSaving(true);
    setEditError('');
    try {
      await kuendigungenApi.vorlagen.update(id, {
        ...editForm,
        platzhalter_json: editForm.platzhalter_json.trim() || null,
      });
      cancelEdit();
      await load();
    } catch {
      setEditError('Änderungen konnten nicht gespeichert werden.');
    } finally {
      setEditSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(
      `Vorlage "${name}" wirklich löschen?\nDiese Aktion kann nicht rückgängig gemacht werden.`
    )) return;
    try {
      await kuendigungenApi.vorlagen.delete(id);
      await load();
    } catch {
      setError(`Vorlage "${name}" konnte nicht gelöscht werden.`);
    }
  };

  // ── Render ──

  if (loading) {
    return (
      <div style={pageStyle}>
        <div style={loadBox}>Vorlagen werden geladen...</div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      {/* Page Header */}
      <div style={pageHeader}>
        <button type="button" onClick={() => navigate(-1)} className="btn-secondary" style={{ flex: '0 0 auto', marginTop: 8 }}>
          ← Zurück
        </button>
        <div style={{ minWidth: 0 }}>
          <div style={eyebrow}>Release 2.3 · Kündigungsassistent</div>
          <h1 style={pageTitleStyle}>Kündigungsvorlagen</h1>
          <p style={pageSubtitle}>
            Verfügbare Platzhalter:{' '}
            <span style={{ color: '#60a5fa' }}>{PLATZHALTER_HINT}</span>
          </p>
        </div>
      </div>

      {/* Global error banner */}
      {error && (
        <div style={errorBanner}>
          <span>{error}</span>
          <button type="button" onClick={() => setError('')} style={closeBtnStyle}>✕</button>
        </div>
      )}

      {/* ── System-Vorlagen ── */}
      <section style={sectionStyle}>
        <div style={sectionHeader}>
          <div style={sectionLeft}>
            <h2 style={sectionTitle}>System-Vorlagen</h2>
            <span style={countBadge}>{systemVorlagen.length}</span>
            <span style={sectionHint}>Schreibgeschützt · systemseitig bereitgestellt</span>
          </div>
        </div>
        {systemVorlagen.length === 0
          ? <EmptyState text="Keine System-Vorlagen vorhanden." />
          : (
            <div style={cardGrid}>
              {systemVorlagen.map(v => (
                <SystemCard key={v.id} v={v} />
              ))}
            </div>
          )
        }
      </section>

      {/* ── Meine Vorlagen ── */}
      <section style={sectionStyle}>
        <div style={sectionHeader}>
          <div style={sectionLeft}>
            <h2 style={sectionTitle}>Meine Vorlagen</h2>
            <span style={countBadge}>{meineVorlagen.length}</span>
          </div>
          {!showNew && (
            <button type="button" onClick={openNewForm} className="btn-primary">
              + Neue Vorlage
            </button>
          )}
        </div>

        {/* New form — rendered above the grid, full width */}
        {showNew && (
          <div style={{ marginBottom: 16 }}>
            <VorlageFormCard
              title="Neue Vorlage erstellen"
              form={newForm}
              onChange={handleNewChange}
              onSubmit={handleNewSubmit}
              onCancel={cancelNew}
              saving={newSaving}
              formError={newError}
            />
          </div>
        )}

        {meineVorlagen.length === 0 && !showNew
          ? (
            <EmptyState text="Noch keine eigenen Vorlagen vorhanden. Klicke auf '+ Neue Vorlage' um zu beginnen." />
          ) : (
            <div style={cardGrid}>
              {meineVorlagen.map(v =>
                editId === v.id
                  ? (
                    <VorlageFormCard
                      key={v.id}
                      title={`"${v.name}" bearbeiten`}
                      form={editForm}
                      onChange={handleEditChange}
                      onSubmit={(e) => { e.preventDefault(); handleEditSave(v.id); }}
                      onCancel={cancelEdit}
                      saving={editSaving}
                      formError={editError}
                      isEdit
                    />
                  ) : (
                    <MeineCard
                      key={v.id}
                      v={v}
                      onEdit={() => startEdit(v)}
                      onDelete={() => handleDelete(v.id, v.name)}
                    />
                  )
              )}
            </div>
          )
        }
      </section>
    </div>
  );
}

// ─── System-Vorlage Card (read-only) ──────────────────────

function SystemCard({ v }) {
  const platzhalter = parsePlatzhalter(v.platzhalter_json);
  const [expanded, setExpanded] = useState(false);

  return (
    <article style={systemCardStyle}>
      <div style={cardHeaderRow}>
        <span style={systemBadge}>System</span>
        <span style={dateMeta}>{formatDate(v.erstellt_am)}</span>
      </div>

      <h3 style={cardTitle}>{v.name}</h3>
      {v.beschreibung && <p style={cardDesc}>{v.beschreibung}</p>}

      <div style={fieldRow}>
        <span style={fieldLabel}>Betreff</span>
        <span style={fieldValue}>{v.betreff || '–'}</span>
      </div>

      {platzhalter.length > 0 && (
        <div style={platzhalterArea}>
          <span style={fieldLabel}>Platzhalter</span>
          <div style={pillRow}>
            {platzhalter.map(p => (
              <span
                key={p.name}
                style={systemPillStyle}
                title={p.beschreibung || p.name}
              >
                {`{{${p.name}}}`}
              </span>
            ))}
          </div>
        </div>
      )}

      <div style={previewArea}>
        <div style={previewHeader}>
          <span style={fieldLabel}>Vorlagentext</span>
          <button type="button" onClick={() => setExpanded(e => !e)} style={toggleBtn}>
            {expanded ? 'Einklappen' : 'Volltext anzeigen'}
          </button>
        </div>
        <pre style={{ ...previewText, maxHeight: expanded ? 'none' : 110 }}>
          {v.text || '(kein Text hinterlegt)'}
        </pre>
      </div>
    </article>
  );
}

// ─── Meine Vorlage Card (editable) ────────────────────────

function MeineCard({ v, onEdit, onDelete }) {
  const platzhalter = parsePlatzhalter(v.platzhalter_json);
  const [expanded, setExpanded] = useState(false);

  return (
    <article style={meineCardStyle}>
      <div style={cardHeaderRow}>
        <span style={meineBadge}>Eigene</span>
        <span style={dateMeta}>
          {v.aktualisiert_am
            ? `Aktualisiert ${formatDate(v.aktualisiert_am)}`
            : `Erstellt ${formatDate(v.erstellt_am)}`
          }
        </span>
      </div>

      <h3 style={cardTitle}>{v.name}</h3>
      {v.beschreibung && <p style={cardDesc}>{v.beschreibung}</p>}

      <div style={fieldRow}>
        <span style={fieldLabel}>Betreff</span>
        <span style={fieldValue}>{v.betreff || '–'}</span>
      </div>

      {platzhalter.length > 0 && (
        <div style={platzhalterArea}>
          <span style={fieldLabel}>Platzhalter</span>
          <div style={pillRow}>
            {platzhalter.map(p => (
              <span
                key={p.name}
                style={meinePillStyle}
                title={p.beschreibung || p.name}
              >
                {`{{${p.name}}}`}
              </span>
            ))}
          </div>
        </div>
      )}

      <div style={previewArea}>
        <div style={previewHeader}>
          <span style={fieldLabel}>Vorlagentext</span>
          <button type="button" onClick={() => setExpanded(e => !e)} style={toggleBtn}>
            {expanded ? 'Einklappen' : 'Volltext anzeigen'}
          </button>
        </div>
        <pre style={{ ...previewText, maxHeight: expanded ? 'none' : 110 }}>
          {v.text || '(kein Text hinterlegt)'}
        </pre>
      </div>

      <div style={cardActions}>
        <button type="button" onClick={onEdit} className="btn-secondary" style={{ flex: 1 }}>Bearbeiten</button>
        <button type="button" onClick={onDelete} className="btn-danger">Löschen</button>
      </div>
    </article>
  );
}

// ─── Vorlage Form Card (new + inline edit) ────────────────

function VorlageFormCard({ title, form, onChange, onSubmit, onCancel, saving, formError, isEdit }) {
  return (
    <div style={formCardStyle}>
      <div style={{ marginBottom: 14 }}>
        <h3 style={formCardTitle}>{title}</h3>
      </div>

      {formError && <div style={frmErrBanner}>{formError}</div>}

      <form onSubmit={onSubmit} style={formGrid}>
        {/* Name */}
        <label style={labelStyle}>
          Name <span style={reqMark}>*</span>
          <input
            type="text"
            value={form.name}
            onChange={e => onChange('name', e.target.value)}
            style={inputStyle}
            placeholder="z. B. Standardkündigung Telekommunikation"
            required
          />
        </label>

        {/* Beschreibung */}
        <label style={labelStyle}>
          Beschreibung
          <input
            type="text"
            value={form.beschreibung}
            onChange={e => onChange('beschreibung', e.target.value)}
            style={inputStyle}
            placeholder="Kurze Beschreibung der Vorlage (optional)"
          />
        </label>

        {/* Betreff — full width */}
        <label style={{ ...labelStyle, gridColumn: '1 / -1' }}>
          Betreff <span style={reqMark}>*</span>
          <input
            type="text"
            value={form.betreff}
            onChange={e => onChange('betreff', e.target.value)}
            style={inputStyle}
            placeholder="z. B. Kündigung meines Vertrages {{VERTRAGSBEZEICHNUNG}} zum {{KUENDIGUNGSDATUM}}"
            required
          />
        </label>

        {/* Vorlagentext — full width */}
        <label style={{ ...labelStyle, gridColumn: '1 / -1' }}>
          Vorlagentext <span style={reqMark}>*</span>
          <textarea
            value={form.text}
            onChange={e => onChange('text', e.target.value)}
            style={{ ...inputStyle, minHeight: 200, resize: 'vertical', fontFamily: 'monospace', fontSize: 13, lineHeight: 1.6 }}
            placeholder={TEXT_EXAMPLE}
            required
          />
          <span style={inputHint}>
            Platzhalter im Format {'{{PLATZHALTER_NAME}}'} werden beim Generieren ersetzt.
          </span>
        </label>

        {/* Platzhalter JSON — full width */}
        <div style={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', gap: 5 }}>
          <span style={{ color: '#94a3b8', fontSize: 12, fontWeight: 800 }}>
            Platzhalter-Definition (JSON, optional)
          </span>
          <textarea
            value={form.platzhalter_json}
            onChange={e => onChange('platzhalter_json', e.target.value)}
            style={{ ...inputStyle, minHeight: 90, resize: 'vertical', fontFamily: 'monospace', fontSize: 12, lineHeight: 1.6 }}
            placeholder={PLATZHALTER_JSON_EXAMPLE}
          />
          <div style={hintBox}>
            <span style={{ color: '#60a5fa', fontSize: 11, fontWeight: 800 }}>Vorgeschlagene Platzhalter: </span>
            <span style={{ color: '#64748b', fontSize: 11, fontFamily: 'monospace' }}>{PLATZHALTER_HINT}</span>
          </div>
        </div>

        {/* Actions */}
        <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="btn-secondary"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={saving}
            className="btn-primary"
            style={{ opacity: saving ? 0.6 : 1, cursor: saving ? 'not-allowed' : 'pointer' }}
          >
            {saving
              ? 'Wird gespeichert...'
              : isEdit
                ? 'Änderungen speichern'
                : 'Vorlage erstellen'
            }
          </button>
        </div>
      </form>
    </div>
  );
}

// ─── Small shared components ───────────────────────────────

function EmptyState({ text }) {
  return <div style={emptyState}>{text}</div>;
}

// ─── Styles ───────────────────────────────────────────────

const pageStyle = { padding: 18, maxWidth: 1200, margin: '0 auto' };
const loadBox = { color: '#94a3b8', padding: 40, textAlign: 'center', fontSize: 15 };
const pageHeader = { display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 20 };
const eyebrow = {
  color: '#93c5fd', fontSize: 11, textTransform: 'uppercase',
  fontWeight: 900, letterSpacing: 1, marginBottom: 6,
};
const pageTitleStyle = { color: '#f8fafc', fontSize: 28, margin: '0 0 6px', fontWeight: 900 };
const pageSubtitle = { color: '#64748b', fontSize: 12, margin: 0, lineHeight: 1.6 };
const errorBanner = {
  background: '#450a0a', border: '1px solid #991b1b', borderRadius: 8,
  padding: '10px 14px', marginBottom: 14, color: '#fecaca',
  display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10,
};
const closeBtnStyle = {
  background: 'none', border: 'none', color: '#fecaca', cursor: 'pointer', fontSize: 16, padding: '0 4px',
};
const sectionStyle = {
  background: '#1e293b', border: '1px solid #334155', borderRadius: 8,
  padding: 20, marginBottom: 16,
};
const sectionHeader = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  gap: 12, marginBottom: 16, flexWrap: 'wrap',
};
const sectionLeft = { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' };
const sectionTitle = { color: '#f8fafc', fontSize: 18, margin: 0, fontWeight: 900 };
const countBadge = {
  color: '#cbd5e1', background: '#0f172a', border: '1px solid #334155',
  borderRadius: 999, padding: '3px 9px', fontSize: 12, fontWeight: 900,
};
const sectionHint = { color: '#475569', fontSize: 12 };
const cardGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 380px), 1fr))',
  gap: 14,
};
const systemCardStyle = {
  background: '#111827', border: '1px solid #263244', borderRadius: 8,
  padding: 16, display: 'flex', flexDirection: 'column', gap: 10,
};
const meineCardStyle = {
  background: '#0f172a', border: '1px solid #1e3a8a55', borderRadius: 8,
  padding: 16, display: 'flex', flexDirection: 'column', gap: 10,
};
const cardHeaderRow = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
};
const systemBadge = {
  background: '#1e293b', color: '#64748b', border: '1px solid #334155',
  borderRadius: 999, padding: '3px 9px', fontSize: 11, fontWeight: 900,
};
const meineBadge = {
  background: '#172554', color: '#93c5fd', border: '1px solid #1e3a8a',
  borderRadius: 999, padding: '3px 9px', fontSize: 11, fontWeight: 900,
};
const dateMeta = { color: '#475569', fontSize: 11 };
const cardTitle = { color: '#f8fafc', fontSize: 16, margin: 0, fontWeight: 900 };
const cardDesc = { color: '#94a3b8', fontSize: 13, margin: 0, lineHeight: 1.5 };
const fieldRow = { display: 'flex', flexDirection: 'column', gap: 3 };
const fieldLabel = {
  color: '#64748b', fontSize: 11, fontWeight: 800,
  textTransform: 'uppercase', letterSpacing: 0.5,
};
const fieldValue = { color: '#cbd5e1', fontSize: 14, lineHeight: 1.4 };
const platzhalterArea = { display: 'flex', flexDirection: 'column', gap: 6 };
const pillRow = { display: 'flex', flexWrap: 'wrap', gap: 6 };
const systemPillStyle = {
  display: 'inline-flex', alignItems: 'center',
  background: '#1e293b', color: '#94a3b8', border: '1px solid #334155',
  borderRadius: 999, padding: '3px 8px', fontSize: 11,
  fontFamily: 'monospace', cursor: 'default',
};
const meinePillStyle = {
  display: 'inline-flex', alignItems: 'center',
  background: '#172554', color: '#93c5fd', border: '1px solid #1e3a8a',
  borderRadius: 999, padding: '3px 8px', fontSize: 11,
  fontFamily: 'monospace', cursor: 'default',
};
const previewArea = { display: 'flex', flexDirection: 'column', gap: 6, flex: 1 };
const previewHeader = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
};
const toggleBtn = {
  background: 'none', border: 'none', color: '#60a5fa',
  cursor: 'pointer', fontSize: 11, padding: 0, fontWeight: 800,
};
const previewText = {
  margin: 0, color: '#cbd5e1', background: '#0f172a', border: '1px solid #263244',
  borderRadius: 6, padding: 10, fontSize: 12, fontFamily: 'monospace',
  lineHeight: 1.6, overflowY: 'auto', overflowX: 'auto',
  whiteSpace: 'pre-wrap', wordBreak: 'break-word',
};
const cardActions = { display: 'flex', gap: 8, marginTop: 4 };
const formCardStyle = {
  background: '#111827', border: '1px solid #1e3a8a', borderRadius: 8, padding: 18,
};
const formCardTitle = { color: '#f8fafc', fontSize: 15, margin: 0, fontWeight: 900 };
const frmErrBanner = {
  background: '#450a0a', border: '1px solid #991b1b', borderRadius: 8,
  padding: '10px 14px', marginBottom: 12, color: '#fecaca', fontSize: 13,
};
const formGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))',
  gap: 12,
};
const labelStyle = {
  display: 'flex', flexDirection: 'column', gap: 5,
  color: '#94a3b8', fontSize: 12, fontWeight: 800,
};
const reqMark = { color: '#f87171', display: 'inline' };
const inputStyle = {
  background: '#1e293b', border: '1px solid #334155', borderRadius: 6,
  padding: '10px 12px', color: '#f8fafc', fontSize: 14,
  width: '100%', boxSizing: 'border-box', outline: 'none',
};
const inputHint = { color: '#475569', fontSize: 11, marginTop: 2 };
const hintBox = {
  background: '#0f172a', border: '1px solid #263244', borderRadius: 6,
  padding: '8px 10px', lineHeight: 1.6,
};
const emptyState = {
  color: '#64748b', background: '#0f172a', border: '1px dashed #263244',
  borderRadius: 8, padding: 18, fontSize: 13, textAlign: 'center',
};
